// client/src/components/react-dashboard/store-management/productUtils.js

export const FITMENT_OPTIONS = [
  'TLS-200',
  'TLS-250',
  'TLS-250I',
  'TLS-300',
  'TLS-350',
  'TLS-350R',
  'TLS-450',
  'TLS-450PLUS',
  'GILBARCO_EMC_PROPLUS',
  'GILBARCO_EMC_PROMAX',
  'EMC',
  'INCON',
  'OMNTEC',
  'FRANKLIN_FUELING',
  'OTHER',
];

export const CATEGORIES = ['hardware', 'software', 'subscription', 'service', 'bundle'];
export const STATUSES = ['draft', 'active', 'hidden', 'archived'];
export const BILLING_TYPES = ['quote', 'one_time', 'monthly', 'annual'];

export const emptyProduct = {
  name: '',
  slug: '',
  sku: '',
  category: 'hardware',
  status: 'draft',
  featured: false,
  sortOrder: 0,
  tagline: '',
  shortDescription: '',
  description: '',
  images: [],
  videos: [],
  pricing: {
    billingType: 'quote',
    currency: 'USD',
    basePrice: '',
    monthlyPrice: '',
    annualPrice: '',
    setupFee: '',
    displayPriceText: 'Contact for pricing',
  },

  features: [
    {
      title: '',
      description: '',
      icon: '',
      sortOrder: 1,
    },
  ],

  sections: [
    {
      heading: '',
      body: '',
      bulletsText: '',
      sortOrder: 1,
    },
  ],

  includedItemsText: '',

  technicalSpecs: {
    screenSize: '',
    connectivityText: '',
    powerRequirements: '',
    mounting: '',
    operatingSystem: '',
    supportedExportsText: '',
    supportedConnectionsText: '',
    warranty: '',
  },

  inventory: {
    trackInventory: false,
    quantityOnHand: 0,
    allowBackorder: true,
  },

  seo: {
    title: '',
    description: '',
    keywordsText: '',
  },
};

export const slugify = (value = '') => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const splitLines = (value = '') => {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
};

export const optionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? undefined : numberValue;
};

export const getProductId = (product) => product?._id || product?.id || product?.slug;

export const normalizeImageUrl = (image) => {
  if (!image) return '';

  if (image.url) return image.url;

  if (image.s3Key) {
    return `https://usfuel-web.s3.us-east-2.amazonaws.com/${image.s3Key
      .split('/')
      .map(encodeURIComponent)
      .join('/')}`;
  }

  return '';
};

export const getYouTubeId = (value = '') => {
  if (!value) return '';

  const trimmed = value.trim();

  // Already just an ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
    return trimmed;
  }

  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/shorts\/([^?&]+)/,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match?.[1]) return match[1];
  }

  return '';
};

export const getYouTubeEmbedUrl = (video) => {
  if (!video) return '';

  if (video.embedUrl) return video.embedUrl;

  const youtubeId = video.youtubeId || getYouTubeId(video.url);

  if (!youtubeId) return '';

  return `https://www.youtube.com/embed/${youtubeId}`;
};

export const getPrimaryVideo = (product) => {
  if (!product?.videos?.length) return null;

  return (
    product.videos.find((video) => video.isPrimary) ||
    [...product.videos].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))[0]
  );
};

export const productToFormState = (product) => {
  return {
    ...emptyProduct,

    name: product?.name || '',
    slug: product?.slug || '',
    sku: product?.sku || '',
    category: product?.category || 'hardware',
    status: product?.status || 'draft',
    featured: Boolean(product?.featured),
    sortOrder: product?.sortOrder ?? 0,

    tagline: product?.tagline || '',
    shortDescription: product?.shortDescription || '',
    description: product?.description || '',
    images: product?.images || [],
    videos: product?.videos?.length
      ? product.videos.map((video, index) => ({
        provider: video.provider || 'youtube',
        title: video.title || '',
        description: video.description || '',
        url: video.url || '',
        embedUrl: video.embedUrl || '',
        youtubeId: video.youtubeId || '',
        thumbnailUrl: video.thumbnailUrl || '',
        isPrimary: Boolean(video.isPrimary) || index === 0,
        sortOrder: video.sortOrder ?? index,
      }))
      : [],

    pricing: {
      billingType: product?.pricing?.billingType || 'quote',
      currency: product?.pricing?.currency || 'USD',
      basePrice: product?.pricing?.basePrice ?? '',
      monthlyPrice: product?.pricing?.monthlyPrice ?? '',
      annualPrice: product?.pricing?.annualPrice ?? '',
      setupFee: product?.pricing?.setupFee ?? '',
      displayPriceText: product?.pricing?.displayPriceText || 'Contact for pricing',
    },

    features: product?.features?.length
      ? product.features.map((feature, index) => ({
        title: feature.title || '',
        description: feature.description || '',
        icon: feature.icon || '',
        sortOrder: feature.sortOrder ?? index + 1,
      }))
      : emptyProduct.features,

    sections: product?.sections?.length
      ? product.sections.map((section, index) => ({
        heading: section.heading || '',
        body: section.body || '',
        bulletsText: section.bullets?.join('\n') || '',
        sortOrder: section.sortOrder ?? index + 1,
      }))
      : emptyProduct.sections,

    includedItemsText: product?.includedItems?.join('\n') || '',

    technicalSpecs: {
      screenSize: product?.technicalSpecs?.screenSize || '',
      connectivityText: product?.technicalSpecs?.connectivity?.join('\n') || '',
      powerRequirements: product?.technicalSpecs?.powerRequirements || '',
      mounting: product?.technicalSpecs?.mounting || '',
      operatingSystem: product?.technicalSpecs?.operatingSystem || '',
      supportedExportsText: product?.technicalSpecs?.supportedExports?.join('\n') || '',
      supportedConnectionsText: product?.technicalSpecs?.supportedConnections?.join('\n') || '',
      warranty: product?.technicalSpecs?.warranty || '',
    },

    inventory: {
      trackInventory: Boolean(product?.inventory?.trackInventory),
      quantityOnHand: product?.inventory?.quantityOnHand ?? 0,
      allowBackorder: product?.inventory?.allowBackorder ?? true,
    },

    seo: {
      title: product?.seo?.title || '',
      description: product?.seo?.description || '',
      keywordsText: product?.seo?.keywords?.join(', ') || '',
    },
  };
};

export const fitmentToSelection = (product) => {
  const selected = {};

  product?.fitment?.forEach((fitment) => {
    if (fitment?.atgModel) {
      selected[fitment.atgModel] = true;
    }
  });

  return selected;
};

export const buildProductPayload = (product, selectedFitment) => {
  const fitment = Object.entries(selectedFitment)
    .filter(([, checked]) => checked)
    .map(([atgModel]) => ({
      atgModel,
      label: atgModel,
      compatible: true,
    }));

  return {
    name: product.name.trim(),
    slug: slugify(product.slug || product.name),
    sku: product.sku.trim().toUpperCase(),
    category: product.category,
    status: product.status,
    featured: product.featured,
    sortOrder: Number(product.sortOrder || 0),

    tagline: product.tagline,
    shortDescription: product.shortDescription,
    description: product.description,

    images: product.images.map((image, index) => ({
      url: normalizeImageUrl(image),
      s3Key: image.s3Key,
      alt: image.alt || product.name,
      isPrimary: Boolean(image.isPrimary),
      sortOrder: index,
    })),
    videos: product.videos
      .filter((video) => video.url || video.youtubeId || video.embedUrl)
      .map((video, index) => {
        const youtubeId = video.youtubeId || getYouTubeId(video.url);

        return {
          provider: video.provider || 'youtube',
          title: video.title,
          description: video.description,
          url: video.url,
          embedUrl: video.embedUrl || (youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : ''),
          youtubeId,
          thumbnailUrl:
            video.thumbnailUrl ||
            (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : ''),
          isPrimary: Boolean(video.isPrimary) || index === 0,
          sortOrder: index,
        };
      }),

    pricing: {
      billingType: product.pricing.billingType,
      currency: product.pricing.currency || 'USD',
      basePrice: optionalNumber(product.pricing.basePrice),
      monthlyPrice: optionalNumber(product.pricing.monthlyPrice),
      annualPrice: optionalNumber(product.pricing.annualPrice),
      setupFee: optionalNumber(product.pricing.setupFee),
      displayPriceText: product.pricing.displayPriceText,
    },

    fitment,

    features: product.features
      .filter((feature) => feature.title.trim())
      .map((feature, index) => ({
        title: feature.title.trim(),
        description: feature.description,
        icon: feature.icon,
        sortOrder: Number(feature.sortOrder || index + 1),
      })),

    sections: product.sections
      .filter((section) => section.heading.trim())
      .map((section, index) => ({
        heading: section.heading.trim(),
        body: section.body,
        bullets: splitLines(section.bulletsText),
        sortOrder: Number(section.sortOrder || index + 1),
      })),

    includedItems: splitLines(product.includedItemsText),

    technicalSpecs: {
      screenSize: product.technicalSpecs.screenSize,
      connectivity: splitLines(product.technicalSpecs.connectivityText),
      powerRequirements: product.technicalSpecs.powerRequirements,
      mounting: product.technicalSpecs.mounting,
      operatingSystem: product.technicalSpecs.operatingSystem,
      supportedExports: splitLines(product.technicalSpecs.supportedExportsText),
      supportedConnections: splitLines(product.technicalSpecs.supportedConnectionsText),
      warranty: product.technicalSpecs.warranty,
    },

    inventory: {
      trackInventory: product.inventory.trackInventory,
      quantityOnHand: Number(product.inventory.quantityOnHand || 0),
      allowBackorder: product.inventory.allowBackorder,
    },

    seo: {
      title: product.seo.title,
      description: product.seo.description,
      keywords: product.seo.keywordsText
        .split(',')
        .map((keyword) => keyword.trim())
        .filter(Boolean),
    },
  };
};